import { useEffect, useMemo, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useForm, SubmitHandler, Controller } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup"
import AsyncSelect from "react-select/async"

import { PageHeader } from "@/widgets/page-header"
import { PageSubtitle } from "@/shared/ui/page-subtitle"
import { Modal } from "@/shared/ui/modal"
import { Loader } from "@/shared/ui/loader"
import { FormInput } from "@/shared/ui/form-input"
import { NavigationCard } from "./navigation-card"

import {
    getSocialProgramById,
    updateSocialProgram,
    getSocialProgramCategories,
    getSocialProgramRegions,
    getFoundations,
    donateToUser,
    distributeToMembers,
    donateToProgram,
    ISocialProgram,
    ISocialProgramCategory,
    ISocialProgramRegion,
    IFoundation,
} from "@/shared/api/social-programs"
import { getUsers } from "@/shared/api/users"
import { getDisplayName } from "@/entities/user"
import { selectBonuses, fromNanoToUnit, toNano } from "@/entities/bonus"
import { useAppSelector } from "@/shared/lib/redux"
import notify from "@/shared/lib/notify"
import { getErrorMessage } from "@/shared/consts/error-codes"

import GroupsSvg from "@/shared/assets/images/UserList.svg"
import WalletSvg from "@/shared/assets/images/Wallet.svg"
import ArrowDown from "@/shared/assets/images/ArrowDown.svg"

const editSchema = yup.object({
    name: yup.string().required('Укажите название').min(1),
    description: yup.string().max(2048, 'Не более 2048 символов'),
})

const donateUserSchema = yup.object({
    user_id: yup.string().required('Укажите ID пользователя'),
    bonus_id: yup.string().required('Выберите бонус'),
    amount: yup.number().typeError('Укажите число').required('Укажите сумму').positive('Сумма должна быть больше 0'),
    comment: yup.string(),
})

const distributeSchema = yup.object({
    bonus_id: yup.string().required('Выберите бонус'),
    amount: yup.number().typeError('Укажите число').required('Укажите сумму').positive('Сумма должна быть больше 0'),
})

const donateProgramSchema = yup.object({
    target_program_id: yup.string().required('Выберите программу'),
    bonus_id: yup.string().required('Выберите бонус'),
    amount: yup.number().typeError('Укажите число').required('Укажите сумму').positive('Сумма должна быть больше 0'),
    comment: yup.string(),
})

type EditValues = yup.InferType<typeof editSchema>
type DonateUserValues = yup.InferType<typeof donateUserSchema>
type DistributeValues = yup.InferType<typeof distributeSchema>
type DonateProgramValues = yup.InferType<typeof donateProgramSchema>
type ActiveModal = 'donateUser' | 'distribute' | 'donateProgram' | null

const SummaryRow = ({ label, value }: { label: string; value: string }) => (
    <div>
        <div className="font-semibold uppercase text-[11px] text-[#828282] tracking-wide">{label}</div>
        <div className="text-color-dark mt-0.5">{value}</div>
    </div>
)

interface ProgramSummaryProps {
    program: ISocialProgram
    categoryName: string
    regionName: string
}

const ProgramSummary = ({ program, categoryName, regionName }: ProgramSummaryProps) => (
    <div className="box flex flex-col gap-4">
        <div className="flex gap-4 items-start">
            {program.logo ? (
                <div className="w-[80px] h-[80px] rounded-xl overflow-hidden shrink-0 border">
                    <img src={program.logo} alt="" className="w-full h-full object-contain" />
                </div>
            ) : (
                <div className="w-[80px] h-[80px] rounded-xl shrink-0 border bg-gray-100 flex items-center justify-center text-gray-400 text-2xl font-bold">
                    {program.name?.[0] ?? '?'}
                </div>
            )}
            <div className="flex-1 min-w-0">
                <div className="text-xl font-semibold leading-tight">{program.name}</div>
                <div className="text-sm text-gray-500 mt-1">{program.description}</div>
            </div>
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <SummaryRow label="Тип" value={program.type === 'foundation' ? 'Фонд' : 'Пользовательская'} />
            <SummaryRow label="Статус" value={program.approved ? 'Одобрена' : 'На рассмотрении'} />
            {categoryName && <SummaryRow label="Категория" value={categoryName} />}
            {regionName && <SummaryRow label="Регион" value={regionName} />}
            <SummaryRow label="Участников" value={String(program.total_users)} />
            <SummaryRow label="Баланс" value={`${fromNanoToUnit(program.total_wallet_balance)} ед.`} />
        </div>
    </div>
)

type UserOption = { value: string; label: string }

const loadUserOptions = (inputValue: string): Promise<UserOption[]> =>
    getUsers({ page: 1, limit: 50, search: inputValue || undefined, all: true })
        .then(r => r.data.items.map(u => ({ value: u.id, label: getDisplayName(u) })))
        .catch((): UserOption[] => [])

export const SocialProgramDetailPage = () => {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const bonuses = useAppSelector(selectBonuses)

    const [program, setProgram] = useState<ISocialProgram | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [categories, setCategories] = useState<ISocialProgramCategory[]>([])
    const [regions, setRegions] = useState<ISocialProgramRegion[]>([])
    const [isEditing, setIsEditing] = useState(false)
    const [editLogo, setEditLogo] = useState<File | null>(null)

    const [foundations, setFoundations] = useState<IFoundation[]>([])
    const [activeModal, setActiveModal] = useState<ActiveModal>(null)
    const [selectedUserOption, setSelectedUserOption] = useState<UserOption | null>(null)

    const isFoundation = program?.type === 'foundation'
    const isAdminOwned = program?.owner_user_id === null

    const editForm = useForm<EditValues>({
        defaultValues: { name: '', description: '' },
        resolver: yupResolver(editSchema),
    })

    const donateUserForm = useForm<DonateUserValues>({
        defaultValues: { user_id: '', bonus_id: '', amount: 0, comment: '' },
        resolver: yupResolver(donateUserSchema),
    })

    const distributeForm = useForm<DistributeValues>({
        defaultValues: { bonus_id: '', amount: 0 },
        resolver: yupResolver(distributeSchema),
    })

    const donateProgramForm = useForm<DonateProgramValues>({
        defaultValues: { target_program_id: '', bonus_id: '', amount: 0, comment: '' },
        resolver: yupResolver(donateProgramSchema),
    })

    const bonusOptions = useMemo(() => bonuses.map(b => ({ value: b.id, label: b.name })), [bonuses])

    const foundationOptions = useMemo(
        () => foundations.filter(f => f.id !== id).map(f => ({ value: f.id, label: f.name })),
        [foundations, id],
    )

    const bonusValueContainer = (props: any) => (
        <>
            {props.hasValue && (
                <div className="w-8 h-8 flex justify-center mr-2 items-center rounded-full overflow-hidden shrink-0">
                    <img
                        src={bonuses.find(b => b.id === props.getValue()?.[0]?.value)?.image}
                        className="w-full object-contain h-full"
                        alt=""
                    />
                </div>
            )}
            {props.children}
        </>
    )

    const loadProgram = async () => {
        if (!id) return
        setIsLoading(true)
        try {
            const res = await getSocialProgramById(id)
            setProgram(res.data)
            editForm.reset({ name: res.data.name ?? '', description: res.data.description ?? '' })
        } catch {
            notify.error('Не удалось загрузить программу')
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        loadProgram()
        getSocialProgramCategories().then(r => setCategories(r.data)).catch(() => {})
        getSocialProgramRegions().then(r => setRegions(r.data)).catch(() => {})
    }, [id])

    useEffect(() => {
        if (isFoundation && isAdminOwned) {
            getFoundations().then(r => setFoundations(r.data)).catch(() => {})
        }
    }, [program?.id])

    const handleSave: SubmitHandler<EditValues> = async (data) => {
        if (!id) return
        try {
            if (editLogo) {
                const fd = new FormData()
                fd.append('name', data.name)
                fd.append('description', data.description ?? '')
                fd.append('logo', editLogo)
                await updateSocialProgram(id, fd)
            } else {
                await updateSocialProgram(id, { name: data.name, description: data.description })
            }
            notify.success('Сохранено')
            setIsEditing(false)
            setEditLogo(null)
            loadProgram()
        } catch (err) {
            notify.error(getErrorMessage(err))
        }
    }

    const closeModal = (modal: ActiveModal) => {
        setActiveModal(null)
        if (modal === 'donateUser') { donateUserForm.reset(); setSelectedUserOption(null) }
        if (modal === 'distribute') distributeForm.reset()
        if (modal === 'donateProgram') donateProgramForm.reset()
    }

    const handleDonateUser: SubmitHandler<DonateUserValues> = async (data) => {
        if (!id) return
        try {
            await donateToUser(id, {
                user_id: data.user_id,
                bonus_id: data.bonus_id,
                amount_nano: toNano(data.amount),
                comment: data.comment || undefined,
            })
            notify.success('Бонусы переведены пользователю')
            closeModal('donateUser')
        } catch (err) {
            notify.error(getErrorMessage(err))
        }
    }

    const handleDistribute: SubmitHandler<DistributeValues> = async (data) => {
        if (!id) return
        try {
            const res = await distributeToMembers(id, {
                bonus_id: data.bonus_id,
                amount_nano: toNano(data.amount),
            })
            notify.success(`Распределено ${res.data.length} участникам`)
            closeModal('distribute')
        } catch (err) {
            notify.error(getErrorMessage(err))
        }
    }

    const handleDonateProgram: SubmitHandler<DonateProgramValues> = async (data) => {
        if (!id) return
        try {
            await donateToProgram(id, data.target_program_id, {
                bonus_id: data.bonus_id,
                amount_nano: toNano(data.amount),
                comment: data.comment || undefined,
            })
            notify.success('Пожертвование совершено')
            closeModal('donateProgram')
        } catch (err) {
            notify.error(getErrorMessage(err))
        }
    }

    const getCategoryName = (catId: string | null | undefined) =>
        categories.find(c => c.id === catId)?.name ?? ''
    const getRegionName = (regId: string | null | undefined) =>
        regions.find(r => r.id === regId)?.name ?? ''

    if (isLoading) {
        return (
            <div className="_page_container">
                <PageHeader title="Программа" />
                <div className="info_panel"><Loader /></div>
            </div>
        )
    }

    if (!program) {
        return (
            <div className="_page_container">
                <PageHeader title="Программа" />
                <div className="info_panel">Программа не найдена</div>
            </div>
        )
    }

    if (!isAdminOwned) {
        return (
            <div className="_page_container">
                <PageHeader title={program.name} />
                <div className="flexbox">
                    <PageSubtitle title="Социальная программа" />
                    <ProgramSummary
                        program={program}
                        categoryName={getCategoryName(program.category_id)}
                        regionName={getRegionName(program.region_id)}
                    />
                </div>
            </div>
        )
    }

    return (
        <div className="_page_container">
            <PageHeader title={program.name} />
            <div className="flexbox">
                <PageSubtitle title="Настройки" />
                {isEditing ? (
                    <div className="box">
                        <form onSubmit={editForm.handleSubmit(handleSave)} className="flex flex-col gap-3">
                            <div className="flex flex-col gap-1">
                                <label className="font-semibold uppercase text-[12px] text-[#828282]">Логотип</label>
                                {program.logo && !editLogo && (
                                    <div className="w-[100px] h-[100px] border rounded-xl overflow-hidden mb-1">
                                        <img src={program.logo} alt="" className="object-contain w-full h-full" />
                                    </div>
                                )}
                                <input
                                    type="file"
                                    accept="image/jpeg,image/png"
                                    onChange={e => setEditLogo(e.target.files?.[0] ?? null)}
                                />
                            </div>
                            <FormInput
                                register={editForm.register}
                                label="Название"
                                id="name"
                                required
                                placeholder="Введите название"
                                error={editForm.formState.errors.name}
                            />
                            <FormInput
                                register={editForm.register}
                                label="Описание"
                                id="description"
                                type="textarea"
                                required={false}
                                placeholder="Введите описание"
                                rows={4}
                                maxLength={2048}
                                error={editForm.formState.errors.description}
                            />
                            <div className="flex gap-3 pt-1">
                                <button
                                    type="button"
                                    className="form_cancel_button"
                                    onClick={() => {
                                        setIsEditing(false)
                                        setEditLogo(null)
                                        editForm.reset({ name: program.name, description: program.description })
                                    }}
                                >
                                    Отмена
                                </button>
                                <button
                                    type="submit"
                                    className="form_handle_button"
                                    disabled={editForm.formState.isSubmitting}
                                >
                                    {editForm.formState.isSubmitting ? 'Сохранение...' : 'Сохранить'}
                                </button>
                            </div>
                        </form>
                    </div>
                ) : (
                    <ProgramSummary
                        program={program}
                        categoryName={getCategoryName(program.category_id)}
                        regionName={getRegionName(program.region_id)}
                    />
                )}
                
                {!isEditing && (
                    <button
                        className="form_handle_button !w-auto px-4"
                        onClick={() => setIsEditing(true)}
                    >
                        Редактировать
                    </button>
                )}

                <div className="grid grid-cols-12 gap-3">
                    <div className="col-span-12 screen:col-span-6">
                        <NavigationCard
                            icon={GroupsSvg}
                            title="Группы"
                            subtitle={`Управление группами и приглашениями`}
                            onClick={() => navigate(`/social-programs/${id}/groups`)}
                        />
                    </div>
                    <div className="col-span-12 screen:col-span-6">
                        <NavigationCard
                            icon={WalletSvg}
                            title="Кошелёк"
                            subtitle={`Баланс: ${fromNanoToUnit(program.total_wallet_balance)} ед.`}
                            onClick={() => navigate(`/social-programs/${id}/wallet`)}
                        />
                    </div>
                </div>

                {isFoundation && (
                    <>
                        <PageSubtitle title="Операции с кошельком" />
                        <div className="grid grid-cols-12 gap-3">
                            <button
                                className="col-span-12 screen:col-span-4 box text-left hover:bg-gray-50 transition"
                                onClick={() => setActiveModal('donateUser')}
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <div className="font-semibold">Перевод пользователю</div>
                                </div>
                                <div className="text-sm text-gray-500">Перевести бонусы конкретному участнику</div>
                            </button>
                            <button
                                className="col-span-12 screen:col-span-4 box text-left hover:bg-gray-50 transition"
                                onClick={() => setActiveModal('distribute')}
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <div className="font-semibold">Распределить участникам</div>
                                </div>
                                <div className="text-sm text-gray-500">Равномерно разделить между всеми</div>
                            </button>
                            <button
                                className="col-span-12 screen:col-span-4 box text-left hover:bg-gray-50 transition"
                                onClick={() => setActiveModal('donateProgram')}
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <div className="font-semibold">Пожертвование программе</div>
                                </div>
                                <div className="text-sm text-gray-500">Перевести бонусы другой программе</div>
                            </button>
                        </div>
                    </>
                )}


            </div>

            <Modal
                isOpen={activeModal === 'donateUser'}
                title="Перевод пользователю"
                subtitle="Перевести бонусы с кошелька фонда пользователю"
                onRequestClose={() => closeModal('donateUser')}
            >
                <form onSubmit={donateUserForm.handleSubmit(handleDonateUser)} className="flex flex-col gap-3">
                    <div className={`flex relative flex-col gap-[6px] pb-[8px] border-b-[1.5px] border-[#B9C9C6] ${donateUserForm.formState.errors.user_id ? 'border-color-danger' : ''}`}>
                        <div className="flex justify-between w-full items-center">
                            <label className="font-semibold uppercase text-[12px] text-[#828282]">Пользователь</label>
                            {donateUserForm.formState.errors.user_id && (
                                <span className="text-xs text-color-danger">{donateUserForm.formState.errors.user_id.message}</span>
                            )}
                        </div>
                        <Controller
                            control={donateUserForm.control}
                            name="user_id"
                            render={({ field }) => (
                                <AsyncSelect
                                    classNames={{
                                        control: () => "w-full !outline-0 !shadow-none !min-h-[auto] !border-0 !border-none !p-0 !outline-none",
                                        valueContainer: () => "!border-0 !outline-0 !p-0 text-color-dark text-lg",
                                        placeholder: () => "text-color-dark/70 text-base !m-0",
                                        input: () => "!m-0 !p-0",
                                        menuPortal: () => "!z-[9999]",
                                    }}
                                    loadOptions={loadUserOptions}
                                    value={selectedUserOption?.value === field.value ? selectedUserOption : (field.value ? { value: field.value, label: field.value } : null)}
                                    onChange={option => {
                                        const o = option as UserOption | null
                                        field.onChange(o?.value ?? '')
                                        setSelectedUserOption(o)
                                    }}
                                    placeholder="Введите имя или телефон для поиска..."
                                    noOptionsMessage={() => "Начните вводить для поиска"}
                                    menuPosition="fixed"
                                    menuPortalTarget={document.body}
                                    components={{
                                        DropdownIndicator: () => <ArrowDown height={20} width={20} />,
                                        IndicatorSeparator: () => null,
                                    }}
                                />
                            )}
                        />
                    </div>
                    <FormInput
                        label="Бонус"
                        id="bonus_id"
                        type="select"
                        required
                        placeholder="Выберите бонус"
                        options={bonusOptions}
                        control={donateUserForm.control}
                        error={donateUserForm.formState.errors.bonus_id}
                        components={{ ValueContainer: bonusValueContainer }}
                    />
                    <FormInput
                        register={donateUserForm.register}
                        label="Количество бонусов"
                        id="amount"
                        type="number"
                        required
                        placeholder="Укажите количество"
                        min={0}
                        error={donateUserForm.formState.errors.amount}
                    />
                    <FormInput
                        register={donateUserForm.register}
                        label="Комментарий (опционально)"
                        id="comment"
                        required={false}
                        placeholder="Введите комментарий"
                        error={donateUserForm.formState.errors.comment}
                    />
                    <div className="flex gap-3 mt-1">
                        <button type="button" className="form_cancel_button" onClick={() => closeModal('donateUser')}>
                            Отмена
                        </button>
                        <button type="submit" className="form_handle_button" disabled={donateUserForm.formState.isSubmitting}>
                            {donateUserForm.formState.isSubmitting ? 'Перевод...' : 'Перевести'}
                        </button>
                    </div>
                </form>
            </Modal>

            <Modal
                isOpen={activeModal === 'distribute'}
                title="Распределить участникам"
                subtitle="Равномерно разделить сумму между всеми участниками всех групп"
                onRequestClose={() => closeModal('distribute')}
            >
                <form onSubmit={distributeForm.handleSubmit(handleDistribute)} className="flex flex-col gap-3">
                    <FormInput
                        label="Бонус"
                        id="bonus_id"
                        type="select"
                        required
                        placeholder="Выберите бонус"
                        options={bonusOptions}
                        control={distributeForm.control}
                        error={distributeForm.formState.errors.bonus_id}
                        components={{ ValueContainer: bonusValueContainer }}
                    />
                    <FormInput
                        register={distributeForm.register}
                        label="Количество бонусов"
                        id="amount"
                        type="number"
                        required
                        placeholder="Укажите количество"
                        min={0}
                        error={distributeForm.formState.errors.amount}
                    />
                    <div className="flex gap-3 mt-1">
                        <button type="button" className="form_cancel_button" onClick={() => closeModal('distribute')}>
                            Отмена
                        </button>
                        <button type="submit" className="form_handle_button" disabled={distributeForm.formState.isSubmitting}>
                            {distributeForm.formState.isSubmitting ? 'Распределение...' : 'Распределить'}
                        </button>
                    </div>
                </form>
            </Modal>

            <Modal
                isOpen={activeModal === 'donateProgram'}
                title="Пожертвование фонду"
                subtitle="Перевести бонусы на кошелёк другой социальной программы"
                onRequestClose={() => closeModal('donateProgram')}
            >
                <form onSubmit={donateProgramForm.handleSubmit(handleDonateProgram)} className="flex flex-col gap-3">
                    <FormInput
                        label="Программа-получатель"
                        id="target_program_id"
                        type="select"
                        required
                        placeholder="Выберите программу"
                        options={foundationOptions}
                        control={donateProgramForm.control}
                        error={donateProgramForm.formState.errors.target_program_id}
                    />
                    <FormInput
                        label="Бонус"
                        id="bonus_id"
                        type="select"
                        required
                        placeholder="Выберите бонус"
                        options={bonusOptions}
                        control={donateProgramForm.control}
                        error={donateProgramForm.formState.errors.bonus_id}
                        components={{ ValueContainer: bonusValueContainer }}
                    />
                    <FormInput
                        register={donateProgramForm.register}
                        label="Количество бонусов"
                        id="amount"
                        type="number"
                        required
                        placeholder="Укажите количество"
                        min={0}
                        error={donateProgramForm.formState.errors.amount}
                    />
                    <FormInput
                        register={donateProgramForm.register}
                        label="Комментарий (опционально)"
                        id="comment"
                        required={false}
                        placeholder="Введите комментарий"
                        error={donateProgramForm.formState.errors.comment}
                    />
                    <div className="flex gap-3 mt-1">
                        <button type="button" className="form_cancel_button" onClick={() => closeModal('donateProgram')}>
                            Отмена
                        </button>
                        <button type="submit" className="form_handle_button" disabled={donateProgramForm.formState.isSubmitting}>
                            {donateProgramForm.formState.isSubmitting ? 'Отправка...' : 'Пожертвовать'}
                        </button>
                    </div>
                </form>
            </Modal>

        </div>
    )
}
